<template>
  <web-app-layout :showLogo="true" :noPadding="true" :embedded="isEmbedded">
    <div v-if="!isAuthenticated && !isEmbedded" class="auth-redirect">
      <SmartBanner />
    </div>

    <div class="profile-container" :class="{ embedded: isEmbedded }">
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <!-- Global Loading Indicator -->
      <div v-if="initialLoading || loading" class="profile-content-container">
        <profile-page-skeleton />
      </div>

      <template v-else>
        <div v-if="isAuthenticated && !isSlugRoute && !orgId && organizations.length === 0 && !searchLoading" class="loading-container initial-loading">
          <p>Redirecting...</p>
        </div>
        <div v-else class="profile-content-container">
          <template v-if="state.organization">
            <!-- Embedded view - Simple avatar and title -->
            <div v-if="isEmbedded" class="embedded-profile-header">
              <div class="embedded-profile-avatar">
                <img
                  :src="state.organization?.avatar || defaultAvatar"
                  alt="Organization Avatar"
                  @error="handleAvatarError"
                />
              </div>
              <div class="embedded-profile-name-section">
                <h1 class="embedded-user-name">{{ state.organization.name }}</h1>
                <p class="embedded-user-title" v-if="state.organization.organization_type">
                  {{ formatOrgType }}
                </p>
              </div>
            </div>

            <!-- Normal view - Background with Avatar and Name on top -->
            <div
              v-else
              class="profile-header"
            >
              <div class="profile-header-rings" aria-hidden="true" />
              <div class="profile-content-overlay">
                <div class="profile-avatar">
                  <img
                    :src="state.organization?.avatar || defaultAvatar"
                    alt="Organization Avatar"
                    @error="handleAvatarError"
                  />
                </div>
                <div class="profile-name-section">
                  <h1 class="user-name">{{ state.organization.name }}</h1>
                  <p class="user-title" v-if="state.organization.organization_type">
                    {{ formatOrgType }}
                  </p>
                </div>
              </div>
            </div>

            <div class="tabs-container" :class="{ 'tabs-container--shop': activeTab === 'shop' }">
              <div class="tab-buttons">
                <div class="tab-navigation">
                  <button
                    type="button"
                    :class="['tab-button', { active: activeTab === 'about' }]"
                    @click="activeTab = 'about'"
                  >
                    ABOUT
                  </button>
                  <button
                    type="button"
                    :class="['tab-button', { active: activeTab === 'shop' }]"
                    @click="activeTab = 'shop'"
                  >
                    SHOP
                  </button>
                </div>
                <div v-if="activeTab === 'shop'" class="search-container">
                  <div class="figma-sticker-search">
                    <img src="@/assets/icons/searchIcon.svg" alt="" />
                    <input
                      v-model="searchString"
                      type="text"
                      placeholder="Search stickers..."
                      @input="handleStickerSearch(searchString)"
                    />
                  </div>
                </div>
              </div>

              <div v-if="activeTab === 'shop'" class="shop-filter-row">
                <div class="shop-filter-chips">
                  <button
                    v-for="filter in shopFilterOptions"
                    :key="filter.value"
                    type="button"
                    :class="['shop-filter-chip', { active: shopFilter === filter.value }]"
                    @click="setShopFilter(filter.value)"
                  >
                    {{ filter.label }}
                  </button>
                </div>
                <shop-org-filter-dropdown
                  v-if="showOrgFilter"
                  v-model="selectedShopOrgId"
                  :orgs="shopFilterOrgs"
                  :hide-label="true"
                  align="end"
                />
              </div>
            </div>

              <!-- About Tab Content -->
              <div v-if="activeTab === 'about'" class="tab-content">
                <div class="profile-info-section">
                  <div class="bio-section" v-if="state.organization?.purpose">
                    <h2 class="purpose-title">{{ state.organization.purpose }}</h2>
                  </div>

                  <div class="bio-section" v-if="state.organization?.about">
                    <p>{{ state.organization.about }}</p>
                  </div>

                  <!-- Values Section -->
                  <div class="values-section" v-if="state.organization?.values?.length">
                    <h3>VALUES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="value in state.organization.values"
                        :key="value.id"
                        class="value-tag"
                      >
                        {{ value.value }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Bullhorns Section -->
                  <div class="values-section" v-if="state.organization?.bullhorns?.length">
                    <h3>CAUSES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="bullhorn in state.organization.bullhorns"
                        :key="bullhorn.id"
                        class="interest-tag"
                      >
                        {{ bullhorn.bullhorn }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Channels Section -->
                  <div class="values-section" v-if="state.organization?.channels?.length">
                    <h3>CHANNELS</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="channel in state.organization.channels"
                        :key="channel.id"
                        class="channel-tag"
                      >
                        {{ channel.channel }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div
                    class="contact-section"
                    v-if="state.organization.email || state.organization.einNumber"
                  >
                    <h3>CONTACT INFORMATION</h3>
                    <div class="contact-info">
                      <div class="info-item" v-if="state.organization?.email">
                        <i class="el-icon-message" />
                        <span>{{ state.organization.email }}</span>
                      </div>
                      <div class="info-item" v-if="state.organization?.einNumber">
                        <i class="el-icon-document" />
                        <span>EIN: {{ state.organization.einNumber }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Shop Tab Content -->
              <div v-if="activeTab === 'shop'" class="tab-content">
                <div class="profile-info-section">
                  <div class="stickers-section">
                    <div
                      class="stickers-container"
                      :class="{ 'has-shop-cart-bar': hasSelectedStickers && !isEmbedded }"
                    >
                      <div v-if="stickerSearchLoading" class="sticker-search-loading">
                        <el-icon :size="30" class="loading-spinner">
                          <Loading />
                        </el-icon>
                        <p>Searching stickers...</p>
                      </div>
                      <div
                        v-else-if="filteredStickers.length === 0 && searchString"
                        class="no-search-results"
                      >
                        <p>No stickers found matching "{{ searchString }}"</p>
                      </div>
                      <div
                        v-else-if="filteredStickers.length === 0 && selectedShopOrgId"
                        class="no-search-results"
                      >
                        <p>No stickers found for this organization</p>
                      </div>
                      <div
                        v-else-if="filteredStickers.length === 0"
                        class="no-search-results"
                      >
                        <p>No stickers yet</p>
                      </div>
                      <stickers-grid-template
                        v-else
                        :stickers="filteredStickers"
                        :selectable="!isEmbedded"
                        :selected-ids="selectedStickerIds"
                        :selected-quantities="selectedStickerQuantities"
                        @toggle-select="toggleStickerSelection"
                        @update-quantity="updateStickerQuantity"
                      />
                    </div>
                  </div>
                </div>

                <Teleport to="body">
                  <div
                    v-if="hasSelectedStickers && !isEmbedded"
                    class="shop-cart-bar"
                    :class="{ 'shop-cart-bar--embedded': isEmbedded }"
                  >
                    <div class="shop-cart-bar-inner">
                      <div class="shop-cart-bar-summary">
                        <p class="shop-cart-bar-count">
                          {{ selectedStickerCount }}
                          {{ selectedStickerCount === 1 ? 'sticker' : 'stickers' }}
                          ·
                          {{ selectedItemCount }}
                          {{ selectedItemCount === 1 ? 'item' : 'items' }}
                        </p>
                        <p v-if="selectedDonationSubtotal > 0" class="shop-cart-bar-total">
                          Estimated total with fees: {{ selectedTotalWithFeesLabel }}
                        </p>
                        <p v-else class="shop-cart-bar-total shop-cart-bar-total--free">
                          Free stickers selected
                        </p>
                      </div>
                      <div class="shop-cart-bar-actions">
                        <button
                          type="button"
                          class="shop-cart-bar-clear"
                          @click="clearStickerSelection"
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          class="shop-cart-bar-btn shop-cart-bar-btn--secondary"
                          @click="addSelectedStickersToCart(false)"
                        >
                          Add to cart
                        </button>
                        <button
                          type="button"
                          class="shop-cart-bar-btn"
                          @click="addSelectedStickersToCart(true)"
                        >
                          Go to checkout
                        </button>
                      </div>
                    </div>
                  </div>
                </Teleport>
              </div>
          </template>
        </div>
      </template>
    </div>
    <DialogCartDonationAmount
      v-model="showCartAmountDialog"
      :items="cartAmountDialogItems"
      @confirm="handleCartAmountDialogConfirm"
    />
    <DialogCartChooseCause
      v-model="showCartCauseDialog"
      :items="cartCauseDialogItems"
      @confirm="handleCartCauseDialogConfirm"
    />
  </web-app-layout>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, computed, watch, ref, nextTick,
} from 'vue';
import { Loading, Search } from '@element-plus/icons-vue';
import webAppToast from '@/utils/webAppToast';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import WebAppLayout from '@/views/web-app/dashboard/WebAppLayout.vue';
import ProfilePageSkeleton from '@/views/web-app/components/ProfilePageSkeleton.vue';
import gqlRequest from '@/views/web-app/dashboard/gql-request';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';
import defaultBackgroundImg from '@/assets/images/bg.jpg';
import SmartBanner from '@/views/web-app/sticker/SmartBanner.vue';
import StickersGridTemplate from '@/views/web-app/sticker/StickersGridTemplate.vue';
import ShopOrgFilterDropdown from '@/views/web-app/components/ShopOrgFilterDropdown.vue';
import { isEmbeddedContext, isEmbeddedRoute, requestEmbedHeightSync } from '@/utils/embeddedUtils';
import { navigateToCheckout } from '@/utils/checkoutNavigation';
import { calculatePriceWithFees, getStickerMinimumUnitCost } from '@/utils/priceWithFees';
import DialogCartDonationAmount from '@/views/web-app/sticker/DialogCartDonationAmount.vue';
import DialogCartChooseCause from '@/views/web-app/sticker/DialogCartChooseCause.vue';
import {
  buildCartAmountDialogItem,
  buildShopCartLine,
  shouldPromptCartAmountBeforeAdd,
  shouldShowShopQuantityStepper,
  stickerNeedsCauseSelection,
  stickerMissingCauseData,
  mergeStickerCauseFields,
  CartAmountConfirmEntry,
  CartCauseDialogItem,
} from '@/utils/cartItemAmount';

interface OrganizationData {
  id?: string;
  name?: string;
  avatar?: string;
  background?: string;
  about?: string;
  purpose?: string;
  email?: string;
  einNumber?: string;
  organization_type?: string;
  profit_type?: string;
  nonprofit_type?: string;
  dob?: string;
  onboarded?: boolean;
  active?: boolean;
  approved?: boolean;
  values?: Array<{
    id: string;
    value: string;
  }>;
  bullhorns?: Array<{
    id: string;
    bullhorn: string;
  }>;
  channels?: Array<{
    id: string;
    channel: string;
  }>;
  stickers?: Array<{
    id: string;
    name: string;
    imageUrl: string;
  }>;
}

export default defineComponent({
  name: 'OrganizationProfile',
  components: {
    WebAppLayout,
    ProfilePageSkeleton,
    SmartBanner,
    StickersGridTemplate,
    ShopOrgFilterDropdown,
    DialogCartDonationAmount,
    DialogCartChooseCause,
    Loading,
    Search,
  },
  setup() {
    const {
      loading,
      fetchOrganization,
      fetchAllOrganizations,
      fetchPublicOrganization,
      fetchPublicOrganizationBySlug,
      getSocialShopStickers,
      fetchApprovedOrgStickers,
      getPublicSticker,
    } = gqlRequest();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    type ShopFilter = 'all' | 'myItems' | 'boostedForAwareness';

    const state = reactive({
      organization: null as OrganizationData | null,
      error: null as string | null,
      socialShopStickers: [] as any[],
      inventoryStickers: [] as any[],
    });

    const organizations = ref<{ id: string, name: string, createdAt?: string }[]>([]);
    const filteredOrganizations = ref<{ id: string, name: string }[]>([]);
    const selectedOrgId = ref('');
    const searchLoading = ref(false);
    const currentPage = ref(1);
    const hasMorePages = ref(true);
    const isLoadingMore = ref(false);
    const lastSearchQuery = ref('');
    const initialLoading = ref(true);
    const activeTab = ref('about');
    const searchString = ref('');
    const debouncedSearchString = ref('');
    const searchDebounceTimer = ref<number | null>(null);
    const stickerSearchLoading = ref(false);
    const hasStickers = ref(false);
    const showShopFilters = ref(false);
    const shopFilter = ref<ShopFilter>('all');
    const selectedShopOrgId = ref('');
    const selectedStickerIds = ref<string[]>([]);
    const selectedStickerQuantities = ref<Record<string, number>>({});
    const dropdownScrollListener = ref<(() => void) | null>(null);

    const defaultAvatar = defaultAvatarImg;
    const defaultBackground = defaultBackgroundImg;

    const orgId = computed(() => route.params.id as string);
    const orgSlug = computed(() => route.params.slug as string);
    const isSlugRoute = computed(() => !!orgSlug.value);

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);

    // Check if we're in embedded context
    const isEmbedded = computed(() => isEmbeddedRoute(route.path) || isEmbeddedContext());

    // Check for tab query parameter
    const tabQueryParam = computed(() => route.query.tab as string);

    const isOwnOrgProfile = computed(() => {
      const currentOrgId = state.organization?.id || orgId.value;
      if (!currentOrgId || !organizations.value.length) return false;
      return organizations.value.some((org) => org.id === currentOrgId);
    });

    const shopFilterOptions = computed(() => [
      { value: 'all' as ShopFilter, label: 'All' },
      {
        value: 'myItems' as ShopFilter,
        label: isOwnOrgProfile.value ? 'My Items' : "Org's Items",
      },
      { value: 'boostedForAwareness' as ShopFilter, label: 'Boosted for Awareness' },
    ]);

    const stickersForActiveFilter = computed(() => {
      if (shopFilter.value === 'myItems') {
        return state.inventoryStickers;
      }
      if (shopFilter.value === 'boostedForAwareness') {
        return state.socialShopStickers;
      }
      return [...state.socialShopStickers, ...state.inventoryStickers];
    });

    function getStickerOrgId(sticker: any): string {
      return (sticker?.organization?.name || '').trim();
    }

    function stickerMatchesSearch(sticker: any, query: string): boolean {
      if (!query) return true;
      const title = (sticker?.title || '').toLowerCase();
      const description = (sticker?.description || '').toLowerCase();
      const orgName = (sticker?.organization?.name || '').toLowerCase();
      return title.includes(query) || description.includes(query) || orgName.includes(query);
    }

    const stickersForGridSource = computed(() => {
      const query = debouncedSearchString.value.trim().toLowerCase();
      return stickersForActiveFilter.value.filter((sticker) => (
        stickerMatchesSearch(sticker, query)
      ));
    });

    const shopFilterOrgs = computed(() => {
      const orgsById = new Map<string, { id: string; name: string; avatar?: string }>();

      stickersForGridSource.value.forEach((sticker) => {
        const id = getStickerOrgId(sticker);
        const name = sticker?.organization?.name;
        if (!id || !name || orgsById.has(id)) {
          return;
        }
        orgsById.set(id, {
          id,
          name,
          avatar: sticker.organization?.avatar,
        });
      });

      return [...orgsById.values()].sort((a, b) => a.name.localeCompare(b.name));
    });

    const showOrgFilter = computed(() => (
      (shopFilter.value === 'all' || shopFilter.value === 'boostedForAwareness')
      && shopFilterOrgs.value.length > 0
    ));

    const orgFilterLabel = computed(() => (
      shopFilter.value === 'boostedForAwareness'
        ? 'Featured Organizations'
        : 'Filter by Org'
    ));

    const filteredStickers = computed(() => {
      if (!selectedShopOrgId.value) {
        return stickersForGridSource.value;
      }
      return stickersForGridSource.value.filter(
        (sticker) => getStickerOrgId(sticker) === selectedShopOrgId.value,
      );
    });

    const formatOrgType = computed(() => {
      if (!state.organization?.organization_type) return '';

      const type = state.organization.organization_type;

      return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    });

    function applySmartShopFilter() {
      const hasSocial = state.socialShopStickers.length > 0;
      const hasInventory = state.inventoryStickers.length > 0;

      hasStickers.value = hasSocial || hasInventory;
      selectedShopOrgId.value = '';

      if (hasSocial && hasInventory) {
        showShopFilters.value = true;
        shopFilter.value = 'all';
        return;
      }

      showShopFilters.value = false;
      if (hasSocial) {
        shopFilter.value = 'boostedForAwareness';
      } else if (hasInventory) {
        shopFilter.value = 'myItems';
      } else {
        shopFilter.value = 'all';
      }
    }

    function setShopFilter(value: ShopFilter) {
      shopFilter.value = value;
      selectedShopOrgId.value = '';
      clearStickerSelection();
    }

    function getShopSelectionStorageKey(): string {
      const orgKey = state.organization?.id || orgSlug.value || orgId.value || 'unknown';
      return `org-shop-selection:${orgKey}`;
    }

    function persistShopSelection() {
      if (typeof sessionStorage === 'undefined') return;
      if (!selectedStickerIds.value.length) {
        sessionStorage.removeItem(getShopSelectionStorageKey());
        return;
      }
      sessionStorage.setItem(getShopSelectionStorageKey(), JSON.stringify({
        ids: selectedStickerIds.value,
        quantities: selectedStickerQuantities.value,
      }));
    }

    function restoreShopSelection() {
      if (isEmbedded.value || typeof sessionStorage === 'undefined') return;
      try {
        const raw = sessionStorage.getItem(getShopSelectionStorageKey());
        if (!raw) return;

        const parsed = JSON.parse(raw) as {
          ids?: string[];
          quantities?: Record<string, number>;
        };
        if (!Array.isArray(parsed.ids) || !parsed.ids.length) return;

        const availableIds = new Set(
          stickersForActiveFilter.value.map((sticker) => sticker.id),
        );
        const restoredIds = parsed.ids.filter((id) => availableIds.has(id));
        if (!restoredIds.length) return;

        selectedStickerIds.value = restoredIds;
        const restoredQuantities: Record<string, number> = {};
        restoredIds.forEach((id) => {
          restoredQuantities[id] = Math.max(1, Number(parsed.quantities?.[id]) || 1);
        });
        selectedStickerQuantities.value = restoredQuantities;
      } catch (error) {
        console.error('Failed to restore shop selection:', error);
      }
    }

    function clearStickerSelection() {
      selectedStickerIds.value = [];
      selectedStickerQuantities.value = {};
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(getShopSelectionStorageKey());
      }
    }

    const selectedShopStickers = computed(() => (
      filteredStickers.value.filter((sticker) => selectedStickerIds.value.includes(sticker.id))
    ));

    const hasSelectedStickers = computed(() => selectedStickerIds.value.length > 0);

    const selectedStickerCount = computed(() => selectedStickerIds.value.length);

    const selectedItemCount = computed(() => (
      selectedShopStickers.value.reduce((sum, sticker) => {
        if (shouldShowShopQuantityStepper(sticker)) {
          return sum + (selectedStickerQuantities.value[sticker.id] || 1);
        }
        return sum + 1;
      }, 0)
    ));

    const selectedDonationSubtotal = computed(() => (
      selectedShopStickers.value.reduce((sum, sticker) => {
        const minCost = getStickerMinimumUnitCost(sticker);
        if (shouldShowShopQuantityStepper(sticker)) {
          const quantity = selectedStickerQuantities.value[sticker.id] || 1;
          return sum + (minCost * quantity);
        }
        return sum + minCost;
      }, 0)
    ));

    const selectedTotalWithFees = computed(() => (
      calculatePriceWithFees(selectedDonationSubtotal.value)
    ));

    const selectedTotalWithFeesLabel = computed(() => {
      if (selectedDonationSubtotal.value <= 0) return 'Free';
      return `$${selectedTotalWithFees.value.toFixed(2)}`;
    });

    function toggleStickerSelection(item: any) {
      const id = item?.id;
      if (!id) return;
      const index = selectedStickerIds.value.indexOf(id);
      if (index >= 0) {
        selectedStickerIds.value.splice(index, 1);
        const { [id]: _removedQty, ...rest } = selectedStickerQuantities.value;
        selectedStickerQuantities.value = rest;
        persistShopSelection();
        return;
      }
      selectedStickerIds.value.push(id);
      selectedStickerQuantities.value = {
        ...selectedStickerQuantities.value,
        [id]: 1,
      };
      persistShopSelection();
    }

    function updateStickerQuantity(payload: { item: any; quantity: number }) {
      const id = payload?.item?.id;
      if (!id || !selectedStickerIds.value.includes(id)) return;
      selectedStickerQuantities.value = {
        ...selectedStickerQuantities.value,
        [id]: Math.max(1, payload.quantity),
      };
      persistShopSelection();
    }

    const showCartAmountDialog = ref(false);
    const cartAmountDialogItems = ref<ReturnType<typeof buildCartAmountDialogItem>[]>([]);
    const showCartCauseDialog = ref(false);
    const cartCauseDialogItems = ref<CartCauseDialogItem[]>([]);
    const pendingCartEntries = ref<CartAmountConfirmEntry[]>([]);
    const pendingCartFreeStickers = ref<any[]>([]);
    const pendingGoToCheckout = ref(false);

    function finishAddStickersToCart(
      entries: CartAmountConfirmEntry[],
      goToCheckout: boolean,
    ) {
      if (!entries.length) {
        return;
      }

      entries.forEach((entry) => {
        store.dispatch('cart/addLine', buildShopCartLine(entry.sticker, entry));
      });

      const itemCount = entries.reduce((sum, entry) => sum + (entry.quantity || 1), 0);
      webAppToast.success(
        itemCount === 1
          ? 'Added 1 item to cart'
          : `Added ${itemCount} items to cart`,
      );
      clearStickerSelection();

      if (goToCheckout) {
        navigateToCheckout(router, route, {
          returnLabel: state.organization?.name,
        });
      }
    }

    function buildFreeCartEntries(stickers: any[]): CartAmountConfirmEntry[] {
      return stickers.map((sticker) => ({
        sticker,
        unitCost: getStickerMinimumUnitCost(sticker),
        quantity: shouldShowShopQuantityStepper(sticker)
          ? (selectedStickerQuantities.value[sticker.id] || 1)
          : 1,
      }));
    }

    async function enrichCartEntriesWithCauseData(
      entries: CartAmountConfirmEntry[],
    ): Promise<CartAmountConfirmEntry[]> {
      const idsToFetch = [...new Set(
        entries
          .filter((entry) => stickerMissingCauseData(entry.sticker))
          .map((entry) => entry.sticker.id),
      )];

      if (!idsToFetch.length) {
        return entries;
      }

      const fetchedStickers = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            return await getPublicSticker(id);
          } catch (error) {
            console.error(`Failed to fetch sticker ${id} for cause selection:`, error);
            return null;
          }
        }),
      );

      const detailsById = new Map(
        fetchedStickers
          .filter(Boolean)
          .map((sticker) => [sticker.id, sticker]),
      );

      const enriched = entries.map((entry) => {
        const details = detailsById.get(entry.sticker.id);
        if (!details) {
          return entry;
        }
        return {
          ...entry,
          sticker: mergeStickerCauseFields(entry.sticker, details),
        };
      });

      return enriched;
    }

    async function proceedToCauseSelectionOrFinish(
      entries: CartAmountConfirmEntry[],
      goToCheckout: boolean,
    ) {
      const enrichedEntries = await enrichCartEntriesWithCauseData(entries);
      const entriesNeedingCause = enrichedEntries.filter((entry) => (
        stickerNeedsCauseSelection(entry.sticker)
      ));

      if (!entriesNeedingCause.length) {
        finishAddStickersToCart(enrichedEntries, goToCheckout);
        pendingGoToCheckout.value = false;
        return;
      }

      pendingCartEntries.value = enrichedEntries;
      pendingGoToCheckout.value = goToCheckout;
      cartCauseDialogItems.value = entriesNeedingCause.map((entry) => ({
        sticker: entry.sticker,
        amount: entry.donationAmount ?? entry.unitCost,
        quantity: entry.quantity,
      }));
      showCartCauseDialog.value = true;
    }

    function addSelectedStickersToCart(goToCheckout = false) {
      const stickersToAdd = selectedShopStickers.value;
      if (!stickersToAdd.length) {
        return;
      }

      const promptStickers = stickersToAdd.filter(shouldPromptCartAmountBeforeAdd);
      const freeStickers = stickersToAdd.filter((sticker) => !shouldPromptCartAmountBeforeAdd(sticker));

      pendingGoToCheckout.value = goToCheckout;

      if (!promptStickers.length) {
        proceedToCauseSelectionOrFinish(buildFreeCartEntries(freeStickers), goToCheckout);
        return;
      }

      pendingCartFreeStickers.value = freeStickers;
      cartAmountDialogItems.value = promptStickers.map((sticker) => (
        buildCartAmountDialogItem(
          sticker,
          selectedStickerQuantities.value[sticker.id] || 1,
        )
      ));
      showCartAmountDialog.value = true;
    }

    function handleCartAmountDialogConfirm(entries: CartAmountConfirmEntry[]) {
      const freeEntries = buildFreeCartEntries(pendingCartFreeStickers.value);
      proceedToCauseSelectionOrFinish(
        [...entries, ...freeEntries],
        pendingGoToCheckout.value,
      );
      pendingCartFreeStickers.value = [];
      cartAmountDialogItems.value = [];
    }

    function handleCartCauseDialogConfirm(
      causeSelections: Record<string, string[]>,
    ) {
      const updatedEntries = pendingCartEntries.value.map((entry) => {
        const causeIds = causeSelections[entry.sticker.id];
        if (causeIds?.length) {
          return { ...entry, donationCauseIds: causeIds };
        }
        return entry;
      });

      finishAddStickersToCart(updatedEntries, pendingGoToCheckout.value);
      pendingCartEntries.value = [];
      pendingGoToCheckout.value = false;
      cartCauseDialogItems.value = [];
    }

    // Function to set initial active tab based on query parameter
    function setInitialActiveTab() {
      if (tabQueryParam.value === 'about') {
        activeTab.value = 'about';
        return;
      }
      if (tabQueryParam.value === 'shop' || hasStickers.value) {
        activeTab.value = 'shop';
        return;
      }
      activeTab.value = 'about';
    }

    function selectMostRecentOrganization() {
      if (!organizations.value.length) {
        return;
      }

      const firstOrg = organizations.value[0];
      if (firstOrg && firstOrg.id) {
        selectedOrgId.value = firstOrg.id;

        router.push(`/web/profile/organization/${firstOrg.id}`);
      }
    }

    function handleOrgChange(id: string) {
      if (!id) {
        if (organizations.value.length > 0) {
          selectMostRecentOrganization();
        }
        return;
      }

      if (id && id !== orgId.value) {
        router.push(`/web/profile/organization/${id}`);
      }
    }

    async function searchOrganizations(query: string) {
      if (searchLoading.value) return;

      lastSearchQuery.value = query || '';
      searchLoading.value = true;

      try {
        // Reset pagination for new search
        currentPage.value = 1;
        hasMorePages.value = true;

        if (!query || query.length === 0) {
          // Load initial organizations without search
          const orgs = await fetchAllOrganizations(50, 0, '');
          if (orgs && orgs.length) {
            organizations.value = orgs;
            filteredOrganizations.value = orgs;
            hasMorePages.value = orgs.length === 50;
          }
        } else if (query.length >= 2) {
          // Search with query
          const orgs = await fetchAllOrganizations(50, 0, query);
          if (orgs && orgs.length) {
            organizations.value = orgs;
            filteredOrganizations.value = orgs;
            hasMorePages.value = orgs.length === 50;
          } else {
            organizations.value = [];
            filteredOrganizations.value = [];
            hasMorePages.value = false;
          }
        }
      } catch (error) {
        console.error('Error searching organizations:', error);
        organizations.value = [];
        filteredOrganizations.value = [];
        hasMorePages.value = false;
      } finally {
        searchLoading.value = false;
      }
    }

    async function loadMoreOrganizations() {
      if (isLoadingMore.value || !hasMorePages.value || lastSearchQuery.value) {
        return;
      }

      try {
        isLoadingMore.value = true;
        currentPage.value += 1;

        const skip = (currentPage.value - 1) * 50;
        const orgs = await fetchAllOrganizations(50, skip, lastSearchQuery.value);

        if (orgs && orgs.length) {
          // Append new organizations, avoiding duplicates
          const existingIds = new Set(organizations.value.map((org) => org.id));
          const newOrgs = orgs.filter((org) => !existingIds.has(org.id));

          organizations.value.push(...newOrgs);
          filteredOrganizations.value = [...organizations.value];
          hasMorePages.value = orgs.length === 50;
        } else {
          hasMorePages.value = false;
        }
      } catch (error) {
        console.error('Error loading more organizations:', error);
        hasMorePages.value = false;
      } finally {
        isLoadingMore.value = false;
      }
    }

    function handleDropdownVisibleChange(visible: boolean) {
      if (visible) {
        // Add scroll listener when dropdown opens
        setTimeout(() => {
          const dropdown = document.querySelector('.org-select-dropdown .el-select-dropdown__wrap');
          if (dropdown && !dropdownScrollListener.value) {
            dropdownScrollListener.value = () => {
              const { scrollTop, scrollHeight, clientHeight } = dropdown;
              // Load more when scrolled to bottom (with 50px threshold)
              const shouldLoadMore = scrollTop + clientHeight >= scrollHeight - 50
                && hasMorePages.value && !lastSearchQuery.value;
              if (shouldLoadMore) {
                loadMoreOrganizations();
              }
            };
            dropdown.addEventListener('scroll', dropdownScrollListener.value);
          }
        }, 100);
      } else {
        // Remove scroll listener when dropdown closes
        const dropdown = document.querySelector('.org-select-dropdown .el-select-dropdown__wrap');
        if (dropdown && dropdownScrollListener.value) {
          dropdown.removeEventListener('scroll', dropdownScrollListener.value);
          dropdownScrollListener.value = null;
        }
      }
    }

    async function fetchOrganizationStickers(organizationId: string) {
      try {
        const [socialShopResult, inventoryResult] = await Promise.allSettled([
          getSocialShopStickers({ socialShopOrgId: organizationId }),
          fetchApprovedOrgStickers(organizationId),
        ]);

        state.socialShopStickers = socialShopResult.status === 'fulfilled'
          ? (socialShopResult.value || [])
          : [];
        state.inventoryStickers = inventoryResult.status === 'fulfilled'
          ? (inventoryResult.value || [])
          : [];

        if (socialShopResult.status === 'rejected') {
          console.error('Error fetching social shop stickers:', socialShopResult.reason);
        }
        if (inventoryResult.status === 'rejected') {
          console.error('Error fetching inventory stickers:', inventoryResult.reason);
        }

        applySmartShopFilter();
        restoreShopSelection();
      } catch (error) {
        console.error('Error fetching organization stickers:', error);
        state.socialShopStickers = [];
        state.inventoryStickers = [];
        hasStickers.value = false;
        showShopFilters.value = false;
        shopFilter.value = 'all';
      }
    }

    function handleStickerSearch(query: string) {
      searchString.value = query;

      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      if (!query.trim()) {
        stickerSearchLoading.value = false;
        debouncedSearchString.value = '';
        return;
      }

      stickerSearchLoading.value = true;

      searchDebounceTimer.value = setTimeout(() => {
        debouncedSearchString.value = query.trim();
        stickerSearchLoading.value = false;
      }, 500) as any;
    }

    async function fetchOrganizationProfile() {
      const identifier = orgId.value || orgSlug.value;
      if (!identifier) {
        return;
      }

      try {
        loading.value = true;
        state.error = null;
        hasStickers.value = false;
        state.socialShopStickers = [];
        state.inventoryStickers = [];
        showShopFilters.value = false;
        shopFilter.value = 'all';
        selectedShopOrgId.value = '';
        searchString.value = '';
        debouncedSearchString.value = '';
        let organization = null;

        if (isSlugRoute.value) {
          organization = await fetchPublicOrganizationBySlug(orgSlug.value);
        } else {
          organization = await fetchPublicOrganization(orgId.value);
        }

        if (organization) {
          state.organization = organization;
          initialLoading.value = false;

          const shopOrgId = organization.id || orgId.value;
          if (shopOrgId) {
            await fetchOrganizationStickers(shopOrgId);
          }
          setInitialActiveTab();
        } else {
          state.error = 'Organization not found';
          console.error('Organization not found for identifier:', identifier);
        }
      } catch (error) {
        console.error('Error loading organization profile:', error);
        state.error = 'Failed to load organization data';
        if (error.message?.includes('not authorized') && !isEmbedded.value) {
          const currentPath = route.fullPath;
          router.push(`/auth/web/signin?redirect=${encodeURIComponent(currentPath)}`);
        }
      } finally {
        loading.value = false;
        initialLoading.value = false;
      }
    }

    async function loadOrganizations() {
      if (!isAuthenticated.value) return;

      // Check if user is an org admin or platform admin
      const isOrgAdminValue = store.getters['users/isOrgAdmin'];
      const isAdminValue = store.getters['users/isAdmin'];
      
      console.log('[ORGANIZATION PROFILE] Loading organizations - isOrgAdmin:', isOrgAdminValue, 'isAdmin:', isAdminValue);
      
      if (!isOrgAdminValue && !isAdminValue) {
        console.log('[ORGANIZATION PROFILE] User is not an org admin, redirecting to home');
        router.push('/web');
        return;
      }

      try {
        searchLoading.value = true;
        initialLoading.value = true;
        currentPage.value = 1;
        hasMorePages.value = true;
        const orgs = await fetchAllOrganizations(50, 0, '');

        console.log('[ORGANIZATION PROFILE] Organizations fetched:', orgs?.length || 0);

        if (orgs && orgs.length) {
          organizations.value = orgs;
          filteredOrganizations.value = orgs;
          hasMorePages.value = orgs.length === 50;

          if (orgId.value) {
            selectedOrgId.value = orgId.value;
          } else {
            selectMostRecentOrganization();
          }
        } else {
          // User has no organizations, redirect to home
          console.log('[ORGANIZATION PROFILE] User has no organizations, redirecting to home');
          router.push('/web');
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        searchLoading.value = false;
        setTimeout(() => {
          initialLoading.value = false;
        }, 300);
      }
    }

    onMounted(async () => {
      if (isEmbedded.value) {
        clearStickerSelection();
      }

      // Only load the org-admin picker list when no specific org is requested
      if (isAuthenticated.value && !isSlugRoute.value && !orgId.value) {
        await loadOrganizations();
      }

      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.params.id, (newId) => {
      selectedOrgId.value = newId as string;

      if (newId) {
        fetchOrganizationProfile();
      } else if (organizations.value.length > 0 && !isSlugRoute.value) {
        selectMostRecentOrganization();
      }
    });

    watch(() => route.params.slug, (newSlug) => {
      if (newSlug) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.name, () => {
      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    // Watch for tab query parameter changes
    watch(() => route.query.tab, () => {
      setInitialActiveTab();
    });

    watch(isEmbedded, (embedded) => {
      if (embedded) {
        clearStickerSelection();
      }
    });

    watch(shopFilterOrgs, (orgs) => {
      if (
        selectedShopOrgId.value
        && !orgs.some((org) => org.id === selectedShopOrgId.value)
      ) {
        selectedShopOrgId.value = '';
      }
    });

    watch([shopFilter, selectedShopOrgId, debouncedSearchString], () => {
      clearStickerSelection();
      if (isEmbedded.value) {
        nextTick(() => requestEmbedHeightSync());
      }
    });

    watch(activeTab, async (tab) => {
      if (tab === 'shop' && isEmbedded.value) {
        await nextTick();
        requestEmbedHeightSync();
      }
    });

    watch(filteredStickers, async () => {
      if (isEmbedded.value && activeTab.value === 'shop') {
        await nextTick();
        requestEmbedHeightSync();
      }
    });

    watch([loading, initialLoading], async ([isLoading, isInitialLoading]) => {
      if (isEmbedded.value && !isLoading && !isInitialLoading) {
        await nextTick();
        requestEmbedHeightSync();
      }
    });

    const handleAvatarError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;
      
      // Prevent infinite loop if default image also fails
      if (target.src === defaultAvatar) return;
      
      target.src = defaultAvatar;
    };

    return {
      state,
      loading,
      searchLoading,
      initialLoading,
      stickerSearchLoading,
      defaultAvatar,
      defaultBackground,
      isAuthenticated,
      isEmbedded,
      formatOrgType,
      organizations,
      filteredOrganizations,
      selectedOrgId,
      orgId,
      orgSlug,
      isSlugRoute,
      activeTab,
      searchString,
      hasStickers,
      filteredStickers,
      showShopFilters,
      shopFilter,
      shopFilterOptions,
      showOrgFilter,
      shopFilterOrgs,
      orgFilterLabel,
      selectedShopOrgId,
      selectedStickerIds,
      selectedStickerQuantities,
      hasSelectedStickers,
      selectedStickerCount,
      selectedItemCount,
      selectedDonationSubtotal,
      selectedTotalWithFeesLabel,
      setShopFilter,
      toggleStickerSelection,
      updateStickerQuantity,
      addSelectedStickersToCart,
      showCartAmountDialog,
      cartAmountDialogItems,
      handleCartAmountDialogConfirm,
      showCartCauseDialog,
      cartCauseDialogItems,
      handleCartCauseDialogConfirm,
      clearStickerSelection,
      hasMorePages,
      isLoadingMore,
      lastSearchQuery,
      handleOrgChange,
      searchOrganizations,
      handleStickerSearch,
      handleDropdownVisibleChange,
      loadMoreOrganizations,
      handleAvatarError,
    };
  },
});
</script>

<style lang="scss">
/* Global styles for Element Plus components */
.org-select-dropdown {
  .el-select-dropdown__item {
    padding-left: 0 !important;
  }

  .load-more-option {
    text-align: center !important;
    font-style: italic !important;
    color: #64748b !important;
    background-color: #f8fafc !important;
    border-top: 1px solid #e2e8f0 !important;

    &:hover {
      background-color: #f8fafc !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.org-selector-container {
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
  margin-bottom: 25px;
  padding: 15px;
  background-color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  label {
    font-weight: 600;
    color: #2c3e50;
    min-width: 150px;
  }

  .org-selector {
    flex: 1;
    max-width: 500px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      padding: 1px 30px 1px 0;
    }

    :deep(.el-input__inner) {
      padding-left: 0;
      margin-left: 0;
      text-indent: 0;
      transition: all 0.2s;

      &:hover, &:focus {
        border-color: #005C6B;
      }
    }

    :deep(.el-select__input) {
      margin-left: 0;
      padding-left: 0;
    }

    :deep(.el-select-v2__placeholder),
    :deep(.el-select__placeholder) {
      padding-left: 0;
    }

    :deep(.el-select__tags) {
      margin-left: 0;
      padding-left: 0;
    }
  }

  :deep(.el-select-dropdown__item) {
    padding-left: 16px !important;

    &.selected {
      color: #005C6B;
      font-weight: 500;
    }

    &:hover {
      background-color: #f0f9ff;
    }
  }
}

.profile-container {
  max-width: 100%;
  margin: 0;
  padding: 0;
  padding-top: 24px;
  padding-bottom: 64px;
  text-align: left;
  
  @media (min-width: 768px) {
    padding-top: 32px;
    padding-bottom: 80px;
  }
  
  @media (min-width: 1024px) {
    padding-top: 40px;
    padding-bottom: 96px;
  }

  /* Add padding for embedded views */
  &.embedded {
    padding: 30px;

    @media (max-width: 768px) {
      padding: 20px;
    }
  }
}

.profile-content-container {
  max-width: 1200px;
  margin-top: 20px;
  text-align: left;
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
}

.profile-header {
  height: 176px;
  background: linear-gradient(130deg, #1d8a8a 0%, #1d5f7e 55%, #1a3a5c 100%);
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin: 0 0 28px;
  box-shadow: none;
  display: flex;
  align-items: center;
  padding: 32px 24px;

  @media (min-width: 768px) {
    padding: 32px 40px;
  }

  @media (min-width: 1024px) {
    padding: 32px 32px;
  }

  @media (max-width: 768px) {
    height: 160px;
    padding: 20px 24px;
  }
}

.profile-header-rings {
  position: absolute;
  top: 0;
  right: 0;
  width: 256px;
  height: 100%;
  pointer-events: none;
  opacity: 0.17;
  background:
    radial-gradient(circle at 100% 50%, transparent 40px, rgba(255, 255, 255, 0.9) 41px, transparent 42px),
    radial-gradient(circle at 100% 50%, transparent 62px, rgba(255, 255, 255, 0.9) 63px, transparent 64px),
    radial-gradient(circle at 100% 50%, transparent 84px, rgba(255, 255, 255, 0.9) 85px, transparent 86px),
    radial-gradient(circle at 100% 50%, transparent 106px, rgba(255, 255, 255, 0.9) 107px, transparent 108px),
    radial-gradient(circle at 100% 50%, transparent 128px, rgba(255, 255, 255, 0.9) 129px, transparent 130px);
}

.profile-header-overlay {
  display: none;
}

.profile-content-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding-top: 0;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: flex-start;
    text-align: left;
    padding-top: 0;
    gap: 16px;
  }
}

.profile-avatar {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: none;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 72px;
    height: 72px;
    order: 0;
  }
}

.profile-name-section {
  color: white;
  text-align: left;

  @media (max-width: 768px) {
    text-align: left;
    order: 2;
  }

  .user-name {
    color: white;
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 4px 0;
    letter-spacing: -0.3px;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  .user-title {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    margin: 0;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;

    @media (max-width: 768px) {
      font-size: 10px;
    }
  }
}

.tabs-container {
  margin-bottom: 30px;
  text-align: left;
  padding: 0;

  &.tabs-container--shop {
    .shop-filter-row {
      margin-top: 12px;
      margin-bottom: 0;
      padding-top: 12px;
      border-top: 1px solid #f3f4f6;
    }
  }
}

.tab-buttons {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  position: relative;
  border-bottom: 1px solid #e5e7eb;

  /* Mobile layout - stack vertically */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}

.tab-navigation {
  display: flex;
  align-items: center;
}

.search-container {
  display: flex;
  align-items: center;
  align-self: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    align-self: flex-start;
  }

  .figma-sticker-search {
    position: relative;
    width: 208px;

    img {
      position: absolute;
      left: 12px;
      top: 50%;
      width: 13px;
      height: 13px;
      transform: translateY(-50%);
      opacity: 0.7;
      pointer-events: none;
    }

    input {
      width: 100%;
      padding: 8px 16px 8px 36px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      color: #374151;
      font-size: 13px;
      outline: none;

      &::placeholder {
        color: #9ca3af;
      }

      &:focus {
        border-color: #2bb5a0;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      max-width: 350px;
    }
  }

  .sticker-search {
    width: 280px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #005C6B inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1fbeaf inset;
      }
    }

    :deep(.el-input__inner) {
      font-size: 14px;

      &::placeholder {
        color: #a8a8a8;
      }
    }

    :deep(.el-input__prefix-inner) {
      color: #a8a8a8;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      width: 100%;
      max-width: 350px;
    }
  }
}

.tab-button {
  background: none;
  border: none;
  padding: 12px 0;
  margin-right: 28px;
  margin-bottom: -1px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-transform: uppercase;

  /* Custom indicator that matches text width */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background-color: transparent;
    transition: background-color 0.3s ease;
  }

  &:hover {
    color: #2bb5a0;
  }

    &.active {
    color: #111827;

    &::after {
      background-color: #2bb5a0;
    }
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in;
  padding: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-info-section {
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  h3:not(.shop-section-title) {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }
}

.bio-section {
  text-align: left;
  margin: 30px 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  &:first-child {
    margin-top: 0;
  }

  p {
    color: #4a5568;
    line-height: 1.8;
    margin: 0;
    font-size: 18px;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 15px 0;
  padding: 5px;
}

.value-tag, .interest-tag, .channel-tag {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: transparent !important;
  color: #000000 !important;
  border: 1px solid #000000 !important;

  &:hover {
    transform: scale(1.05);
    background-color: #f5f5f5 !important;
  }
}

.values-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }
}

.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;

      i {
        color: #666;
      }

      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}

.stickers-section {
  text-align: left;
  margin-top: 0;

  h3 {
    margin-top: 0;
  }
}

.stickers-container {
  margin-top: 0;
}

.stickers-container.has-shop-cart-bar {
  padding-bottom: 120px;

  @media (min-width: 640px) {
    padding-bottom: 96px;
  }
}

.shop-cart-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);

  &:not(.shop-cart-bar--embedded) {
    @media (min-width: 1024px) {
      left: 300px;
      width: calc(100% - 300px);
    }
  }
}

.shop-cart-bar-inner {
  width: 100%;
  max-width: 1200px;
  margin: 0;
  padding: 12px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 40px 16px;
  }

  @media (min-width: 1024px) {
    padding: 16px 64px;
  }
}

.shop-cart-bar-summary {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.shop-cart-bar-count {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  line-height: 1.4;
}

.shop-cart-bar-total {
  margin: 2px 0 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;

  &--free {
    font-size: 14px;
    font-weight: 600;
    color: #2bb5a0;
  }
}

.shop-cart-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;

  @media (min-width: 640px) {
    width: auto;
  }
}

.shop-cart-bar-clear {
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: #374151;
  }
}

.shop-cart-bar-btn {
  flex: 1;
  padding: 12px 16px;
  background: #2bb5a0;
  border: none;
  border-radius: 9999px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &--secondary {
    background: #ffffff;
    color: #2bb5a0;
    border: 1px solid #2bb5a0;

    &:hover {
      background: #f0fdfa;
      opacity: 1;
    }
  }

  @media (min-width: 640px) {
    flex: 0 0 auto;
    padding: 12px 20px;
  }

  @media (min-width: 768px) {
    padding: 12px 24px;
    font-size: 15px;
  }
}

.shop-filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 24px;
}

.shop-filter-chips {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  padding: 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.shop-filter-chip {
  flex-shrink: 0;
  background: #ffffff;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.2;
  padding: 6px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #2bb5a0;
  }

  &.active {
    background: #2bb5a0;
    color: #ffffff;
    border-color: #2bb5a0;
    font-weight: 500;
  }
}

.sticker-search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 15px;

  p {
    color: #5c6c7c;
    font-size: 16px;
    margin: 0;
  }

  .loading-spinner {
    color: #1fbeaf;
  }
}

.no-search-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 40px 20px;

  p {
    color: #6b7280;
    font-size: 16px;
    margin: 0;
    text-align: center;
    font-style: italic;
  }
}

.loading-spinner {
  animation: rotate 2s linear infinite;
  color: #1fbeaf;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
  }

  p {
    color: #5c6c7c;
    font-size: 18px;
  }

  &.initial-loading {
    min-height: 50vh;
    background-color: #f8fafc;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

    .loading-spinner {
      color: #005C6B;
    }

    p {
      font-size: 20px;
      font-weight: 500;
    }
  }
}

.auth-redirect {
  text-align: center;
  padding: 60px 24px;
  font-size: 20px;
  color: #5c6c7c;
  background: #f8fafc;
  border-radius: 12px;
  margin: 20px 24px;

  @media (min-width: 768px) {
    padding: 60px 40px;
    margin: 20px 40px;
  }

  @media (min-width: 1024px) {
    padding: 60px 64px;
    margin: 20px 64px;
  }
}

.error-message {
  background-color: #fef2f2;
  color: #ef4444;
  padding: 20px 24px;
  border-radius: 12px;
  margin: 0 24px 25px;
  text-align: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);

  @media (min-width: 768px) {
    padding: 20px 40px;
    margin: 0 40px 25px;
  }

  @media (min-width: 1024px) {
    padding: 20px 64px;
    margin: 0 64px 25px;
  }
}

/* Purpose title styling */
.purpose-title {
  color: #2c3e50;
  font-size: 24px;
  margin: 0 0 20px 0;
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
}

/* Embedded view styles */
.embedded-profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
}

.embedded-profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: #fff;
  border: 2px solid #e5e7eb;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.embedded-profile-name-section {
  .embedded-user-name {
    color: #2c3e50;
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 5px 0;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  .embedded-user-title {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
    font-weight: 500;
  }
}

.embedded-shop-content {
  .profile-info-section {
    padding: 0;
  }

  .stickers-section {
    margin-top: 0;

    h3 {
      margin-top: 0;
    }
  }

  .embedded-search {
    margin-bottom: 0;
    width: 100%;

    .sticker-search {
      width: 100%;
      max-width: 350px;
    }
  }

  .shop-filter-chips {
    padding-top: 12px;
  }

  .shop-filter-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
